import React from 'react'
import { Typography, TextField, Box, Button } from '@mui/material'
import { IStockCardPackaging } from 'interfaces/IStockCard'
import { IPackagingParts } from 'interfaces/IPackaging'
import { VALUE_IN } from 'enums/stockCard'
import LoadPackagingPartAutocomplete from 'shared/autocomplete/loadPackagingPartAutocomplete'
import { formatStockQuantity } from 'components/inventory/helpers'
import { clampDecimalPlaces } from 'utils/clampDecimalPlaces'

interface Props {
  packaging: IStockCardPackaging
  inOutDirection: string
  packagingParts: IPackagingParts[]
  onChange: (packagingPartId: string, value: any, key: string) => void
  disabled?: boolean
  isConfirmed?: boolean
  onRemove?: () => void
  isEditing?: boolean
  outgoingDestination?: string
  isSuperuser?: boolean
}

const FieldRow = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <Box display="flex" borderBottom="1px solid #eee" py={1.5} alignItems="center">
    <Typography style={{ width: '30%', fontWeight: 500, fontSize: '0.95rem', color: '#666' }}>
      {label}
    </Typography>
    <Box flexGrow={1}>
      {children}
    </Box>
  </Box>
)

export default function StockCardPackagingItem(props: Props) {
  const {
    packaging,
    inOutDirection,
    packagingParts,
    onChange,
    disabled,
    isConfirmed,
    onRemove,
    isEditing,
  } = props

  const isEditable = isEditing || !isConfirmed

  const currentStock =
    (packagingParts || []).find(
      (p) =>
        p?.packagingPartId &&
        p.packagingPartId.toLowerCase() ===
          packaging?.packagingPartId?.toLowerCase()
    )?.stock ?? 0

  const qtyLabel = inOutDirection === VALUE_IN ? 'Qty Mau Dimasukan' : 'Qty Diminta'
  const confirmedQtyLabel = inOutDirection === VALUE_IN ? 'Jumlah Diterima' : 'Jumlah Dikeluarkan'
  const currentStockLabel = 'Stok Sekarang'

  return (
    <Box width="100%" py={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Item Packaging
        </Typography>
        {onRemove && (
          <Button color="secondary" onClick={onRemove} size="small" variant="outlined">
            Hapus
          </Button>
        )}
      </Box>

      {isEditable ? (
        <Box>
          <FieldRow label="Kode Bahan">
            {isEditing ? (
              <LoadPackagingPartAutocomplete
                name={packaging.packagingPartId}
                onChange={(e: IPackagingParts) => {
                  onChange(packaging.packagingPartId, e, 'packagingPart')
                }}
              />
            ) : (
              <Typography style={{ fontWeight: 600 }}>{packaging.packagingPartId}</Typography>
            )}
          </FieldRow>

          <FieldRow label={currentStockLabel}>
            {(packagingParts || []).length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                Loading...
              </Typography>
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(currentStock)}</Typography>
            )}
          </FieldRow>

          <FieldRow label={qtyLabel}>
            {isEditing ? (
              <TextField
                value={packaging.quantity}
                onChange={(e) => onChange(packaging.packagingPartId, clampDecimalPlaces(e.target.value), 'quantity')}
                variant="outlined"
                size="small"
                fullWidth
              />
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(packaging.quantity)}</Typography>
            )}
          </FieldRow>

          <FieldRow label={confirmedQtyLabel}>
            <TextField
              required
              autoComplete="off"
              variant="outlined"
              fullWidth
              size="small"
              value={packaging.confirmedQuantity ?? ''}
              onChange={(e) =>
                onChange(
                  packaging.packagingPartId,
                  clampDecimalPlaces(e.target.value),
                  'confirmedQuantity'
                )
              }
              disabled={disabled}
            />
          </FieldRow>
        </Box>
      ) : (
        <Box>
          <FieldRow label="Kode Bahan">
            <Typography style={{ fontWeight: 600 }}>{packaging?.packagingPartId ?? '-'}</Typography>
          </FieldRow>
          <FieldRow label="Nama Bahan">
            <Typography style={{ fontWeight: 600 }}>{packaging.partName || '-'}</Typography>
          </FieldRow>
          <FieldRow label={currentStockLabel}>
            {(packagingParts || []).length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                Loading...
              </Typography>
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(currentStock)}</Typography>
            )}
          </FieldRow>
          <FieldRow label={qtyLabel}>
            <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(packaging.quantity ?? 0)}</Typography>
          </FieldRow>
          <FieldRow label={confirmedQtyLabel}>
            <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(packaging.confirmedQuantity)}</Typography>
          </FieldRow>
        </Box>
      )}
    </Box>
  )
}
